import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import * as yup from 'yup';
import useForm from '@hooks/useForm/useForm.tsx';
import SvgMapPin from '@icons/map-pin.svg?react';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';

export type Address = {
  streetNr: string;
  country: string;
  zipCode: string;
  city: string;
};

interface ComponentProps {
  address: Address;
  readOnly?: boolean;
  onChange?: (address: Address) => void;
}

interface FormValues {
  address: Address;
}

const AddressTag: React.FC<ComponentProps> = ({
                                                address,
                                                readOnly = false,
                                                onChange,
                                              }) => {
  const { tr } = useTranslationLgs();

  const formattedAddress = [address.country, address.zipCode, address.city, address.streetNr].join(' | ');

  const schema = yup.object().shape({
    address: yup.object().shape({
      streetNr: yup.string().required(),
      country: yup.string().required(),
      zipCode: yup.string().required(),
      city: yup.string().required(),
    }),
  });

  const onContinueHandler = (data: FormValues) => {
    onChange?.(data.address);
  };

  const form = useForm<FormValues>(
    schema,
    { address },
    onContinueHandler,
    false,
    false,
    () => {},
    'none',
  );

  return (
    <>
      <ButtonTag
        label={tr('AddressTag.tagAddress', 'Address')}
        value={formattedAddress}
        icon={<SvgMapPin />}
        onSave={onSuccess => {
          form.validateAndSend();
          onSuccess();
        }}
        autoClose
        readonly={readOnly}
      >
        <div className="flex flex-col gap-2">
          <Field
            label={tr('AddressTag.streetNrLabel', 'Street and number')}
            required
            errorMessage={form.errors.address?.streetNr}
          >
            <RingilInputText
              value={form.values.address?.streetNr ?? ''}
              onChange={value => form.setFieldValue('address.streetNr', value)}
              placeholder={tr('AddressTag.streetNrPlaceholder', 'Street and number')}
              fullWidth
            />
          </Field>
          <Field
            label={tr('AddressTag.zipCodeLabel', 'ZIP')}
            required
            errorMessage={form.errors.address?.zipCode}
          >
            <RingilInputText
              value={form.values.address?.zipCode ?? ''}
              onChange={value => form.setFieldValue('address.zipCode', value)}
              placeholder={tr('AddressTag.zipCodePlaceholder', 'ZIP')}
              fullWidth
            />
          </Field>
          <Field
            label={tr('AddressTag.cityLabel', 'City')}
            required
            errorMessage={form.errors.address?.city}
          >
            <RingilInputText
              value={form.values.address?.city ?? ''}
              onChange={value => form.setFieldValue('address.city', value)}
              placeholder={tr('AddressTag.cityPlaceholder', 'City')}
              fullWidth
            />
          </Field>
          <Field
            label={tr('AddressTag.countryLabel', 'Country')}
            required
            errorMessage={form.errors.address?.country}
          >
            <InputCountry
              value={form.values.address?.country ?? ''}
              onChange={v =>  form.setFieldValue('address.country', v)}
            />
          </Field>
        </div>
      </ButtonTag>
    </>
  );
};

export default AddressTag;